'use client';

import React from 'react';

interface ChatWallpaperProps {
  children: React.ReactNode;
}

// Original dark doodle wallpaper — messaging motifs at very low opacity
// Tile: 180×180px, doodles scaled small and densely distributed
const WALLPAPER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 180 180">
  <defs>
    <style>
      .d { fill: none; stroke: #4080C8; stroke-linecap: round; stroke-linejoin: round; }
      .f { fill: #4080C8; }
    </style>
  </defs>

  <!-- Speech bubble 1 (top-left) -->
  <g opacity="0.08" transform="translate(6,8)">
    <rect class="d" x="0" y="0" width="22" height="14" rx="4" stroke-width="1.3"/>
    <polygon class="d" points="4,14 2,20 10,14" stroke-width="1.1"/>
  </g>

  <!-- Paper plane (top-center) -->
  <g opacity="0.07" transform="translate(52,5)">
    <path class="d" d="M0,8 L14,0 L9,14 L5,8 Z" stroke-width="1.3"/>
    <line class="d" x1="5" y1="8" x2="9" y2="14" stroke-width="1.1"/>
  </g>

  <!-- Star (top-right area) -->
  <g opacity="0.07" transform="translate(100,6)">
    <path class="d" d="M7,0 L8.5,4.8 L14,4.8 L9.5,7.8 L11,12.5 L7,9.5 L3,12.5 L4.5,7.8 L0,4.8 L5.5,4.8 Z" stroke-width="1.2"/>
  </g>

  <!-- Heart (top-right) -->
  <g opacity="0.07" transform="translate(140,7)">
    <path class="d" d="M9,3 C9,3 7,0 4.5,0 C2,0 0,2 0,4.5 C0,7.5 4.5,11 9,14 C13.5,11 18,7.5 18,4.5 C18,2 16,0 13.5,0 C11,0 9,3 9,3 Z" stroke-width="1.3"/>
  </g>

  <!-- Rocket (far right) -->
  <g opacity="0.07" transform="translate(162,4)">
    <path class="d" d="M7,0 C7,0 10,2 10,7 L10,12 L4,12 L4,7 C4,2 7,0 7,0 Z" stroke-width="1.2"/>
    <path class="d" d="M4,10 L1,13 L4,12" stroke-width="1.1"/>
    <path class="d" d="M10,10 L13,13 L10,12" stroke-width="1.1"/>
    <circle class="f" cx="7" cy="6" r="1.5" opacity="0.8"/>
  </g>

  <!-- Smiley face (left, row 2) -->
  <g opacity="0.08" transform="translate(4,38)">
    <circle class="d" cx="9" cy="9" r="8" stroke-width="1.3"/>
    <circle class="f" cx="6.5" cy="7.5" r="1.1"/>
    <circle class="f" cx="11.5" cy="7.5" r="1.1"/>
    <path class="d" d="M5.5,11 Q9,14 12.5,11" stroke-width="1.2"/>
  </g>

  <!-- Envelope (row 2) -->
  <g opacity="0.07" transform="translate(34,40)">
    <rect class="d" x="0" y="3" width="20" height="13" rx="2" stroke-width="1.3"/>
    <polyline class="d" points="0,3 10,11 20,3" stroke-width="1.1"/>
  </g>

  <!-- Balloon (row 2 center) -->
  <g opacity="0.07" transform="translate(72,36)">
    <ellipse class="d" cx="8" cy="8" rx="7" ry="9" stroke-width="1.3"/>
    <path class="d" d="M8,17 Q6,21 8,24" stroke-width="1.1"/>
    <line class="d" x1="5" y1="17" x2="11" y2="17" stroke-width="1.1"/>
  </g>

  <!-- Musical note (row 2) -->
  <g opacity="0.07" transform="translate(108,38)">
    <path class="d" d="M6,0 L6,12 M6,12 C6,14 2,15 2,13 C2,11 6,12 6,12 Z" stroke-width="1.3"/>
    <line class="d" x1="6" y1="0" x2="14" y2="-2" stroke-width="1.2"/>
    <path class="d" d="M14,-2 L14,10 M14,10 C14,12 10,13 10,11 C10,9 14,10 14,10 Z" stroke-width="1.2"/>
  </g>

  <!-- Phone (row 2 right) -->
  <g opacity="0.07" transform="translate(144,38)">
    <rect class="d" x="2" y="0" width="11" height="18" rx="2.5" stroke-width="1.3"/>
    <line class="d" x1="5" y1="15" x2="10" y2="15" stroke-width="1.2"/>
    <line class="d" x1="6" y1="2.5" x2="9" y2="2.5" stroke-width="1.2"/>
  </g>

  <!-- Camera (row 2 far right) -->
  <g opacity="0.07" transform="translate(163,39)">
    <rect class="d" x="0" y="3" width="18" height="13" rx="2" stroke-width="1.3"/>
    <circle class="d" cx="9" cy="9.5" r="3.5" stroke-width="1.2"/>
    <rect class="d" x="5" y="0" width="8" height="4" rx="1" stroke-width="1.1"/>
  </g>

  <!-- Typing bubble (row 3 left) -->
  <g opacity="0.08" transform="translate(5,72)">
    <rect class="d" x="0" y="0" width="26" height="14" rx="7" stroke-width="1.3"/>
    <circle class="f" cx="6" cy="7" r="1.5"/>
    <circle class="f" cx="13" cy="7" r="1.5"/>
    <circle class="f" cx="20" cy="7" r="1.5"/>
  </g>

  <!-- Flower (row 3) -->
  <g opacity="0.07" transform="translate(44,70)">
    <circle class="d" cx="9" cy="9" r="3" stroke-width="1.2"/>
    <circle class="d" cx="9" cy="3" r="2.5" stroke-width="1.1"/>
    <circle class="d" cx="9" cy="15" r="2.5" stroke-width="1.1"/>
    <circle class="d" cx="3" cy="9" r="2.5" stroke-width="1.1"/>
    <circle class="d" cx="15" cy="9" r="2.5" stroke-width="1.1"/>
  </g>

  <!-- Planet (row 3 center) -->
  <g opacity="0.07" transform="translate(80,68)">
    <circle class="d" cx="10" cy="10" r="7" stroke-width="1.3"/>
    <ellipse class="d" cx="10" cy="10" rx="14" ry="4" stroke-width="1.1"/>
  </g>

  <!-- Gift box (row 3) -->
  <g opacity="0.07" transform="translate(118,70)">
    <rect class="d" x="0" y="6" width="18" height="12" rx="1.5" stroke-width="1.3"/>
    <rect class="d" x="0" y="3" width="18" height="4" rx="1" stroke-width="1.2"/>
    <path class="d" d="M9,3 C9,3 6,0 4,1 C2,2 3,4 5,4 C7,4 9,3 9,3 Z" stroke-width="1.1"/>
    <path class="d" d="M9,3 C9,3 12,0 14,1 C16,2 15,4 13,4 C11,4 9,3 9,3 Z" stroke-width="1.1"/>
    <line class="d" x1="9" y1="3" x2="9" y2="18" stroke-width="1.1"/>
  </g>

  <!-- Star small (row 3 right) -->
  <g opacity="0.07" transform="translate(152,72)">
    <path class="d" d="M6,0 L7.2,4 L11.5,4 L8,6.5 L9.2,10.5 L6,8 L2.8,10.5 L4,6.5 L0.5,4 L4.8,4 Z" stroke-width="1.2"/>
  </g>

  <!-- Animal (cat face, row 3 far right) -->
  <g opacity="0.07" transform="translate(166,70)">
    <circle class="d" cx="7" cy="9" r="6.5" stroke-width="1.3"/>
    <path class="d" d="M3,3 L1,0 L4,2" stroke-width="1.2"/>
    <path class="d" d="M11,3 L13,0 L10,2" stroke-width="1.2"/>
    <circle class="f" cx="5" cy="9" r="1"/>
    <circle class="f" cx="9" cy="9" r="1"/>
    <path class="d" d="M5.5,11.5 Q7,13 8.5,11.5" stroke-width="1.1"/>
  </g>

  <!-- Speech bubble 2 (row 4 left) -->
  <g opacity="0.08" transform="translate(6,104)">
    <rect class="d" x="0" y="0" width="24" height="15" rx="4" stroke-width="1.3"/>
    <line class="d" x1="4" y1="5" x2="20" y2="5" stroke-width="1.1"/>
    <line class="d" x1="4" y1="9" x2="14" y2="9" stroke-width="1.1"/>
    <polygon class="d" points="5,15 3,21 11,15" stroke-width="1.1"/>
  </g>

  <!-- Paper plane 2 (row 4) -->
  <g opacity="0.07" transform="translate(44,106)">
    <path class="d" d="M0,7 L16,0 L10,14 L6,8 Z" stroke-width="1.3"/>
    <line class="d" x1="6" y1="8" x2="10" y2="14" stroke-width="1.1"/>
  </g>

  <!-- Heart 2 (row 4 center) -->
  <g opacity="0.07" transform="translate(78,106)">
    <path class="d" d="M8,3 C8,3 6,0 4,0 C1.5,0 0,2 0,4 C0,7 4,10 8,13 C12,10 16,7 16,4 C16,2 14.5,0 12,0 C10,0 8,3 8,3 Z" stroke-width="1.3"/>
  </g>

  <!-- Notification bell (row 4) -->
  <g opacity="0.07" transform="translate(114,104)">
    <path class="d" d="M8,0 C8,0 6,1.5 6,4 L4,8 L4,12 L12,12 L12,8 L10,4 C10,1.5 8,0 8,0 Z" stroke-width="1.3"/>
    <path class="d" d="M6,12 Q8,15 10,12" stroke-width="1.2"/>
    <circle class="d" cx="8" cy="0" r="1.5" stroke-width="1.1"/>
  </g>

  <!-- Smiley 2 (row 4 right) -->
  <g opacity="0.07" transform="translate(148,106)">
    <circle class="d" cx="8" cy="8" r="7.5" stroke-width="1.3"/>
    <circle class="f" cx="5.5" cy="6.5" r="1"/>
    <circle class="f" cx="10.5" cy="6.5" r="1"/>
    <path class="d" d="M4.5,10 Q8,13 11.5,10" stroke-width="1.2"/>
  </g>

  <!-- Rocket 2 (row 4 far right) -->
  <g opacity="0.07" transform="translate(166,104)">
    <path class="d" d="M6,0 C6,0 9,2 9,6 L9,11 L3,11 L3,6 C3,2 6,0 6,0 Z" stroke-width="1.2"/>
    <path class="d" d="M3,9 L0,12 L3,11" stroke-width="1.1"/>
    <path class="d" d="M9,9 L12,12 L9,11" stroke-width="1.1"/>
    <circle class="f" cx="6" cy="5.5" r="1.3" opacity="0.8"/>
  </g>

  <!-- Envelope 2 (row 5 left) -->
  <g opacity="0.07" transform="translate(5,138)">
    <rect class="d" x="0" y="3" width="22" height="14" rx="2" stroke-width="1.3"/>
    <polyline class="d" points="0,3 11,12 22,3" stroke-width="1.1"/>
  </g>

  <!-- Star 3 (row 5) -->
  <g opacity="0.07" transform="translate(42,140)">
    <path class="d" d="M7,0 L8.5,4.8 L14,4.8 L9.5,7.8 L11,12.5 L7,9.5 L3,12.5 L4.5,7.8 L0,4.8 L5.5,4.8 Z" stroke-width="1.2"/>
  </g>

  <!-- Balloon 2 (row 5 center) -->
  <g opacity="0.07" transform="translate(76,136)">
    <ellipse class="d" cx="8" cy="8" rx="6.5" ry="8" stroke-width="1.3"/>
    <path class="d" d="M8,16 Q6,20 8,23" stroke-width="1.1"/>
    <line class="d" x1="5" y1="16" x2="11" y2="16" stroke-width="1.1"/>
  </g>

  <!-- Camera 2 (row 5) -->
  <g opacity="0.07" transform="translate(112,138)">
    <rect class="d" x="0" y="3" width="18" height="12" rx="2" stroke-width="1.3"/>
    <circle class="d" cx="9" cy="9" r="3.5" stroke-width="1.2"/>
    <rect class="d" x="5" y="0" width="8" height="4" rx="1" stroke-width="1.1"/>
  </g>

  <!-- Musical note 2 (row 5 right) -->
  <g opacity="0.07" transform="translate(148,138)">
    <path class="d" d="M5,0 L5,11 M5,11 C5,13 1.5,14 1.5,12 C1.5,10 5,11 5,11 Z" stroke-width="1.3"/>
    <line class="d" x1="5" y1="0" x2="12" y2="-2" stroke-width="1.2"/>
    <path class="d" d="M12,-2 L12,9 M12,9 C12,11 8.5,12 8.5,10 C8.5,8 12,9 12,9 Z" stroke-width="1.2"/>
  </g>

  <!-- Planet 2 (row 5 far right) -->
  <g opacity="0.07" transform="translate(163,136)">
    <circle class="d" cx="8" cy="9" r="6" stroke-width="1.3"/>
    <ellipse class="d" cx="8" cy="9" rx="12" ry="3.5" stroke-width="1.1"/>
  </g>
</svg>`;

export default function ChatWallpaper({ children }: ChatWallpaperProps) {
  return (
    <div className="relative flex-1 min-h-0 overflow-hidden chat-wallpaper">
      {/* Pattern layer */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(WALLPAPER_SVG)}")`,
          backgroundSize: '180px 180px',
          backgroundRepeat: 'repeat',
          opacity: 1,
        }}
      />
      {/* Content */}
      <div className="relative z-[1] h-full">
        {children}
      </div>
    </div>
  );
}